/**
 * FOrtQuestionManager (DWA-A 138-1)
 *
 * Responsibility:
 * - Toggle visibility of the fOrt question block
 * - Bind answer events (good/medium/bad)
 * - Live-calculate fOrt and write to hidden fields (fOrt + flags)
 * - Handle manual override (manual input disables questions)
 */
export default class FOrtQuestionManager {
    /**
     * @param {jQuery} form
     * @param {Object} state
     * @param {Object} host instance of ConfigurationForm
     */
   	constructor(form, state, host) {
		this.form = form;
		this.state = state;
		this.host = host;

		// Step containers (Step 4 + Step 5)
		this.$step4 = $('#fOrtStep4');
		this.$step5 = $('#fOrtStep5');
		this.$step4Wrapper = this.$step4.closest('.fortWizardStep4');
		this.$step5Wrapper = this.$step5.closest('.fortWizardStep5');
		this.$container = $('#fOrtQuestionWizard');

		this.questions = [];
		this.wasActive = false;

        // answer level → numeric value
        this.answerValueMap = {
            1: 1.0,  // good
            2: 0.65, // medium
            3: 0.3,  // bad
        };
    }

    decodeHtmlEntities(str) {
        return String(str)
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
    }

    parseQuestionsFromDom() {
        // Find first element providing the JSON (user may move the attribute)
        const $provider = $('[data-fort-questions]').first();
        if (!$provider.length) {
            return [];
        }

        let json = $provider.attr('data-fort-questions');
        if (!json || typeof json !== 'string') {
            return [];
        }

        try {
            json = this.decodeHtmlEntities(json);
            const data = JSON.parse(json);
            return Array.isArray(data) ? data : [];
        } catch (e) {
            console.error('[FOrtQuestionManager] Failed to parse fOrt questions JSON', e);
            return [];
        }
    }

   	init() {
		if (!this.$step4.length && !this.$step5.length && !this.$container.length) {
			return;
		}

		// Keep parsing JSON for debugging, but visibility uses rendered rows to avoid dependency on attribute placement.
		this.questions = this.parseQuestionsFromDom();
		this.bindHandlers();
		this.updateVisibility();
		this.syncFromPersisted();
		if (this.getPersistedFlag('.fortIsDerived')) {
			this.recompute(true);
		}
	}

   	syncFromPersisted() {
		const decision = this.getDecision();
		if (decision === 1) {
			const $select = this.$step4.find('.fortStep4ValueSelect');
			const current = this.getPersistedFOrt();
			if (current !== null && current !== 0) {
				$select.val(String(current));
			}
			this.form.find('#fOrtStep4 .fortDecision[value="1"]').prop('checked', true);
		} else if (decision === 0) {
			this.form.find('#fOrtStep4 .fortDecision[value="0"]').prop('checked', true);
			// Sync answers if derived
			const isDerived = this.getPersistedFlag('.fortIsDerived');
			if (isDerived) {
				// The derived value display is already handled by recompute() in init()
			}
		}

		const useRecommend = this.getPersistedFlag('.fortUseRecommendation');
		const $recommend = this.$container.find('.fortRecommendCheckbox');
		if (useRecommend === true) {
			$recommend.prop('checked', true);
			$recommend.closest('.checkbox-root').addClass('checked');
			this.setManualMode(true);
			// Also re-trigger recompute to update display
			this.recompute();
		}
	}

   	updateVisibility() {
		const isActiveBase = (typeof this.host.isGuideline1381Active === 'function')
			? this.host.isGuideline1381Active()
			: false;

		const kiIsManual = this.getPersistedFlag('.kiIsManual');
		const fKIsManual = this.getPersistedFlag('.fKIsManual');
		const isSkipped = kiIsManual || fKIsManual;

		const isActive = isActiveBase && !isSkipped;

		// Reset values when component becomes inactive (deactivated or skipped)
		if (!isActive && this.wasActive) {
			this.reset();
		}

		const hasRenderedQuestions = this.$container.length
			? this.$container.find('tr[data-question-uid]').length > 0
			: false;

		const decision = this.getDecision();
		this.syncDecisionRadios(decision);

		// Step 4 visible only when effectively active
		if (this.$step4.length) {
			if (isActive) {
				if (this.$step4Wrapper.length) {
					this.$step4Wrapper.show();
				}
				this.$step4.show();
				if (typeof this.host.setInputsDisabled === 'function') {
					this.host.setInputsDisabled(this.$step4, false);
				}
			} else {
				if (typeof this.host.setInputsDisabled === 'function') {
					this.host.setInputsDisabled(this.$step4, true);
				}
				this.$step4.hide();
				if (this.$step4Wrapper.length) {
					this.$step4Wrapper.hide();
				}
			}
		}

		// Step 4 value row only if user says fOrt is available (decision = 1)
		if (this.$step4.length) {
			const $valueRow = this.$step4.find('.fortStep4ValueRow');
			const $select = this.$step4.find('.fortStep4ValueSelect');
			if (decision === 1 && isActive) {
				$valueRow.show();
				$select.prop('required', true);
				// prefill from persisted hidden value if present
				const current = this.getPersistedFOrt();
				if (current !== null && current !== 0) {
					$select.val(String(current));
				}
			} else {
				$valueRow.hide();
				$select.prop('required', false);
			}
		}

		// Step 5 visible only when effectively active AND user selected "No" in Step 4
		const shouldShowStep5 = isActive && decision === 0 && hasRenderedQuestions;
		if (this.$step5.length) {
			if (shouldShowStep5) {
				if (this.$step5Wrapper.length) {
					this.$step5Wrapper.show();
				}
				this.$step5.show();
				if (typeof this.host.setInputsDisabled === 'function') {
					this.host.setInputsDisabled(this.$step5, false);
				}
			} else {
				if (typeof this.host.setInputsDisabled === 'function') {
					this.host.setInputsDisabled(this.$step5, true);
				}
				this.$step5.hide();
				if (this.$step5Wrapper.length) {
					this.$step5Wrapper.hide();
				}
			}
		}

		this.wasActive = isActive;
	}

	syncDecisionRadios(decision) {
		// Do not override user input; only sync if nothing is selected yet.
		const $checked = this.form.find('input.fortDecision:checked');
		if ($checked.length) {
			return;
		}
		if (decision === 0 || decision === 1) {
			this.form.find('input.fortDecision[value="' + String(decision) + '"]').prop('checked', true);
		}

		// Also sync recommend checkbox from persisted state
		const useRecommend = this.getPersistedFlag('.fortUseRecommendation');
		const $recommend = this.$container.find('.fortRecommendCheckbox');
		if (useRecommend === true) {
			$recommend.prop('checked', true);
			$recommend.closest('.checkbox-root').addClass('checked');
		} else {
			$recommend.prop('checked', false);
			$recommend.closest('.checkbox-root').removeClass('checked');
		}
	}

	reset() {
		// Clear decision
		this.form.find('input.fortDecision').prop('checked', false);
		// Clear Step 4 select
		if (this.$step4.length) {
			this.$step4.find('.fortStep4ValueSelect').val('');
			this.$step4.find('.fortStep4ValueRow').hide();
		}
		// Clear Step 5 answers + checkbox
		if (this.$container.length) {
			this.$container.find('.fortAnswerLevel').prop('checked', false);
			this.$container.find('.fortRecommendCheckbox').prop('checked', false);
			this.setManualMode(false);
			this.setCalculatedDisplay(null);
		}
		// Reset persisted values/flags
		this.setFOrtValue(0);
		this.setFlags(false, false, false);
	}

	getDecision() {
		// Step 4 radio decision: 1=yes (available), 0=no (go to questions)
		const $checked = this.form.find('input.fortDecision:checked');
		if ($checked.length) {
			return parseInt($checked.val(), 10);
		}

		// If no radio selected yet, try to infer from persisted flags
		const isManual = this.getPersistedFlag('.fortIsManual');
		const useRecommend = this.getPersistedFlag('.fortUseRecommendation');
		if (isManual === true && useRecommend !== true) {
			return 1;
		}
		const isDerived = this.getPersistedFlag('.fortIsDerived');
		if (isDerived === true || useRecommend === true) {
			return 0;
		}

		return null;
	}

	getPersistedFlag(selector) {
		const $field = this.form.find(selector).first();
		if (!$field.length) {
			return null;
		}
		const raw = String($field.val()).trim();
		if (raw === '') {
			return null;
		}
		return raw === '1' || raw === 'true';
	}

	getPersistedFOrt() {
		const $field = this.form.find('input.fortValue').first();
		if (!$field.length) {
			return null;
		}
		const raw = String($field.val()).trim();
		if (raw === '') {
			return null;
		}
		const value = parseFloat(raw);
		return isNaN(value) ? null : value;
	}

   	bindHandlers() {
		// Step 4 decision
		this.form.on('change', 'input.fortDecision', e => {
			// Without a shared name attribute, radios won't auto-uncheck each other.
			this.form.find('input.fortDecision').not(e.target).prop('checked', false);

			const decision = this.getDecision();
			if (decision === 1) {
				// fOrt provided directly
				this.setFlags(true, false);
				// If a value is already selected, persist immediately
				const selected = this.$step4.length ? String(this.$step4.find('.fortStep4ValueSelect').val() || '').trim() : '';
				if (selected !== '') {
					this.setFOrtValue(parseFloat(selected));
				}
			} else if (decision === 0) {
				// fOrt will be derived in Step 5
				this.setFlags(false, false);
			}
			this.updateVisibility();
			this.recompute();
		});

		// Step 4 value selection
		this.form.on('change', '#fOrtStep4 .fortStep4ValueSelect', e => {
			const raw = String($(e.target).val() || '').trim();
			if (raw === '') {
				return;
			}
			const value = parseFloat(raw);
			if (!isNaN(value)) {
				this.setFlags(true, false);
				this.setFOrtValue(value);
			}
		});

		// Answer selection
		this.form.on('change', '#fOrtQuestionWizard .fortAnswerLevel', () => {
            // If user answers questions, clear manual recommendation and use derived mode
            const $recommend = this.$container.find('.fortRecommendCheckbox');
            if ($recommend.length) {
                $recommend.prop('checked', false);
                $recommend.closest('.checkbox-root').removeClass('checked');
            }

            this.setManualMode(false);
            this.recompute();
        });

      		// Recommendation checkbox
      		this.form.on('change', '#fOrtQuestionWizard .fortRecommendCheckbox', () => {
            const $recommend = this.$container.find('.fortRecommendCheckbox');
            const isChecked = $recommend.prop('checked');
            $recommend.closest('.checkbox-root').toggleClass('checked', isChecked);

            if (isChecked) {
                this.setManualMode(true);
                this.setFOrtValue(0.7);
                this.setFlags(true, false, true);
                this.setCalculatedDisplay(0.7);
            } else {
                this.setManualMode(false);
                this.recompute();
           	}
        });
    }

    setManualMode(enabled) {
        this.$container.find('input').each((i, el) => {
            const $el = $(el);
            if (!$el.hasClass('fortRecommendCheckbox')) {
                $el.prop('disabled', !!enabled);
                if (enabled) {
                    $el.removeAttr('required');
                }
            }
        });
    }

   	recompute(init = false) {
		// Only recompute when Step 5 is actually visible/enabled or this is
		if (!init && (!this.$container.length || (this.$step5.length && !this.$step5.is(':visible')))) {
			return;
		}

        // Recommendation checkbox wins
        const useRecommend = this.$container.find('.fortRecommendCheckbox').prop('checked');
        if (useRecommend) {
            this.setManualMode(true);
            this.setFOrtValue(0.7);
            this.setFlags(true, false, true);
            this.setCalculatedDisplay(0.7);
            return;
        }

        this.setManualMode(false);

        const result = this.computeDerivedFOrt();
        if (result !== null) {
            this.setFOrtValue(result);
            this.setFlags(false, true, false);
            this.setCalculatedDisplay(result);
        } else {
            // Not complete → do not persist a partial value
            this.setFOrtValue(0);
            this.setFlags(false, false, false);
            this.setCalculatedDisplay(null);
        }
    }

    computeDerivedFOrt() {
        // We require all questions to be answered to persist a derived value
        const $rows = this.$container.find('tr[data-question-uid]');
        if (!$rows.length) {
            return null;
        }

        let sum = 0.0;
        let answered = 0;

        $rows.each((i, row) => {
            const $row = $(row);
            const $checked = $row.find('.fortAnswerLevel:checked');
            if ($checked.length) {
                const level = parseInt($checked.val(), 10);
                if (this.answerValueMap[level] !== undefined) {
                    sum += this.answerValueMap[level];
                    answered += 1;
                }
            }
        });

        if (answered === 0) {
            return null;
        }

        if (answered !== $rows.length) {
            return null;
        }

        const avg = sum / answered;
        return Math.round(avg * 100) / 100;
    }

    isValid() {
        if (!this.$step5.length || !this.$step5.is(':visible')) {
            return true;
        }
        const useRecommend = this.$container.find('.fortRecommendCheckbox').prop('checked');
        if (useRecommend) {
            return true;
        }
        return this.computeDerivedFOrt() !== null;
    }

   	setFOrtValue(value) {
		// Persisted hidden fields live in Step 4 slide (not inside the Step 5 question container)
		const $field = this.form.find('input.fortValue').first();
		if (!$field.length) {
			return;
		}
		$field.val(String(value));
		$field.trigger('change');

		// Trigger fK recomputation when fOrt changes
		if (this.host && this.host.fMethodeManager
			&& typeof this.host.fMethodeManager.computeDerivedValues === 'function') {
			this.host.fMethodeManager.computeDerivedValues();
		}
	}

   	setFlags(isManual, isDerived, useRecommend) {
		// Persisted hidden fields live in Step 4 slide
		const $manual = this.form.find('input.fortIsManual').first();
		const $derived = this.form.find('input.fortIsDerived').first();
		const $recommend = this.form.find('input.fortUseRecommendation').first();

		if ($manual.length) {
			$manual.val(isManual ? '1' : '0');
		}
		if ($derived.length) {
			$derived.val(isDerived ? '1' : '0');
		}
		if ($recommend.length) {
			$recommend.val(useRecommend ? '1' : '0');
		}
	}

    setCalculatedDisplay(valueOrNull) {
        const $display = this.$container.find('.fortCalculatedValue');
        if (!$display.length) {
            return;
        }
        if (valueOrNull === null) {
            $display.text('–');
            return;
        }
        $display.text(String(valueOrNull));
    }
}
